import React, { useEffect, useState } from 'react';
import { IDynamicDiplayView } from '@components/widgets/dynamic-display';
import DynamicWidget from '@components/widgets/dynamic-display/DynamicWidget';
import EnhancedButton from '@components/button/EnhancedButton';
import MotorDetailsDrawer from './motor-details-drawer/MotorDetailsDrawer';
import { useQuery } from '@apollo/client';
import { getPolicyMotorDetailsQuery } from './queries';
import { useParams } from 'react-router-dom';
import FormattingService from '@services/FormattingService';
import { IPolicyGeneralData } from '../../..';
import { IMotorLovs, IPolicyMotorDetails } from '.';
import { initialPolicyMotorValues } from './content';
import { mapToPolicyMotorDetails } from './utils';

interface IMotorDetailsWidgetProps {
  generalData: IPolicyGeneralData;
  allowPolicyCorrection: boolean;
  onDataRefresh: () => void;
}

const MotorDetailsWidget: React.FC<IMotorDetailsWidgetProps> = ({
  generalData,
  allowPolicyCorrection,
  onDataRefresh,
}) => {
  const { id: policyId } = useParams<{ id: string }>();

  const [motorInfoDrawerOpen, setMotorInfoDrawerOpen] =
    useState<boolean>(false);
  const [values, setValues] = useState<IPolicyMotorDetails>(
    initialPolicyMotorValues
  );
  const [lovs, setLovs] = useState<IMotorLovs>();

  const {
    refetch: refetchMotorDetails,
    data,
    loading,
  } = useQuery(getPolicyMotorDetailsQuery, {
    variables: {
      policyId: policyId,
    },
  });

  const initialize = () => {
    if (data) {
      const { mappedData, lovs } = mapToPolicyMotorDetails(data);
      setValues(mappedData);
      setLovs(lovs);
    }
  };

  useEffect(() => {
    initialize();
  }, [data]);

  const dynamicView: IDynamicDiplayView = {
    name: 'MotorDetails',
    sections: [
      {
        name: 'MotorDetails',
        title: 'Motor Details',
        properties: {
          nameOnLicenseEn: {
            multiline: false,
            value: values.nameOnLicenseEn,
            name: 'nameOnLicenseEn',
            title: 'Name on License (EN)',
          },
          nameOnLicenseAr: {
            multiline: false,
            value: values.nameOnLicenseAr,
            name: 'nameOnLicenseAr',
            title: 'Name on License (AR)',
          },
          vignetteCode: {
            multiline: false,
            value: values.vignetteCode,
            name: 'vignetteCode',
            title: 'Vignette Code',
            hidden: Number(generalData.line.externalCode) !== 50,
          },
          vignette: {
            multiline: false,
            value: values.vignette?.toString(),
            name: 'vignette',
            title: 'Vignette',
            hidden: Number(generalData.line.externalCode) !== 50,
          },
          plateCode: {
            multiline: false,
            value: values.plateCode?.Title,
            name: 'plateCode',
            title: 'Plate Code',
          },
          plateNumber: {
            multiline: false,
            value: values.plateNumber?.toString(),
            name: 'plateNumber',
            title: 'Plate Number',
          },
          usageType: {
            multiline: false,
            value: values.usageType?.Title,
            name: 'usageType',
            title: 'Usage',
          },
          bodyType: {
            multiline: false,
            value: values.bodyType?.Title,
            name: 'bodyType',
            title: 'Body',
          },
          engineType: {
            multiline: false,
            value: values.engineType?.Title,
            name: 'engineType',
            title: 'Engine Type',
          },
          make: {
            multiline: false,
            value: values.make?.Title,
            name: 'make',
            title: 'Make',
          },
          model: {
            multiline: false,
            value: values.model?.Title,
            name: 'model',
            title: 'Model',
          },
          color: {
            multiline: false,
            value: values.color?.Title,
            name: 'color',
            title: 'Color',
          },
          yearOfMake: {
            multiline: false,
            value: values.yearOfMake?.toString(),
            name: 'yearOfMake',
            title: 'Year of Make',
          },
          engine: {
            multiline: false,
            value: FormattingService.formatEnumToDisplay(values.engine),
            name: 'engine',
            title: 'Engine',
          },
          weight: {
            multiline: false,
            value: values.weight?.toString(),
            name: 'weight',
            title: 'Weight (in Tons)',
            hidden: !['VAN', 'PICKUP', 'TANKER'].includes(
              values.bodyType?.Code
            ),
          },
          engineSize: {
            multiline: false,
            value: values.engineSize?.toString(),
            name: 'engineSize',
            title: 'Engine Size (in cc)',
            hidden: values.bodyType?.Code !== 'MOTORCYCLE',
          },
          seatingCapacity: {
            multiline: false,
            value: values.seatingCapacity?.toString(),
            name: 'seatingCapacity',
            title: 'Seating Capacity',
          },
          chassis: {
            multiline: false,
            value: values.chassis,
            name: 'chassis',
            title: 'Chassis',
          },
          horsePower: {
            multiline: false,
            value: values.horsePower?.toString(),
            name: 'horsePower',
            title: 'Horse Power',
          },
        },
      },
    ],
  };

  return (
    <>
      <DynamicWidget
        hasFourFields={true}
        view={dynamicView}
        loading={loading}
        actions={
          allowPolicyCorrection &&
          !loading && (
            <div>
              <EnhancedButton
                isPrimary
                onClick={() => {
                  setMotorInfoDrawerOpen(true);
                }}
              >
                Modify
              </EnhancedButton>
            </div>
          )
        }
      />
      {motorInfoDrawerOpen && (
        <MotorDetailsDrawer
          open={motorInfoDrawerOpen}
          onClose={() => setMotorInfoDrawerOpen(false)}
          onSuccess={() => {
            onDataRefresh();
          }}
          generalData={generalData}
          data={values}
          lovs={lovs}
        />
      )}
    </>
  );
};

export default MotorDetailsWidget;
